import { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import API_URL from '../config';

function Register() {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useContext(AuthContext);
  const { theme } = useSettings();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const guestId = localStorage.getItem('guestId');
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email, username, password, ...(guestId && { guestId }),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      login(data.user, data.token);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-puzzle-background flex items-center justify-center p-4">
      <div className="bg-puzzle-surface rounded-2xl shadow-xl w-full max-w-md p-8 border border-puzzle-border">
        {/* Logo and Branding */}
        <div className="flex flex-col items-center mb-8">
          <img
            src={theme === 'dark' ? '/images/logo_with_text_dark_mode.png' : '/images/logo_with_text.png'}
            alt="Puzzlists"
            className="h-24"
          />
          <p className="text-puzzle-text-secondary mt-2">Create your account</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-puzzle-danger/10 border border-puzzle-danger/30 rounded-lg p-4 flex items-center gap-3">
            <svg className="h-5 w-5 text-puzzle-danger flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-puzzle-danger">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="register-email" className="block text-sm font-medium text-puzzle-text mb-1">
              Email address
              <input
                id="register-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-lg border border-puzzle-border bg-puzzle-surface focus:ring-2 focus:ring-puzzle-blue focus:border-transparent transition-shadow text-puzzle-text placeholder-puzzle-text-secondary font-normal"
                placeholder="you@example.com"
              />
            </label>
          </div>

          <div>
            <label htmlFor="register-username" className="block text-sm font-medium text-puzzle-text mb-1">
              Username
              <input
                id="register-username"
                name="username"
                type="text"
                autoComplete="username"
                required
                minLength={3}
                maxLength={30}
                pattern="^[a-zA-Z0-9._]+$"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-lg border border-puzzle-border bg-puzzle-surface focus:ring-2 focus:ring-puzzle-blue focus:border-transparent transition-shadow text-puzzle-text placeholder-puzzle-text-secondary font-normal"
                placeholder="Choose a username"
              />
              <p className="mt-1 text-xs text-puzzle-text-secondary font-normal">Letters, numbers, dots, and underscores only</p>
            </label>
          </div>

          <div>
            <label htmlFor="register-password" className="block text-sm font-medium text-puzzle-text mb-1">
              Password
              <div className="relative mt-1">
                <input
                  id="register-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 rounded-lg border border-puzzle-border bg-puzzle-surface focus:ring-2 focus:ring-puzzle-blue focus:border-transparent transition-shadow text-puzzle-text placeholder-puzzle-text-secondary font-normal"
                  placeholder="Min 6 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-puzzle-text-secondary hover:text-puzzle-text"
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-puzzle-text-secondary font-normal">Must be at least 6 characters</p>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-puzzle-blue hover:bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:bg-puzzle-blue/60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && (
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {loading ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center mt-8 text-puzzle-text-secondary">
          Already have an account?
          {' '}
          <Link to="/login" className="font-semibold text-puzzle-blue hover:text-blue-600">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Register;
